import { FC } from 'react';

import { BannerModerationState, getBannerTextTypography } from 'utils/banner/utils';
import { BannerModerationStatus } from 'utils/constants/banner';
import { DOT } from 'utils/constants/common';

import { Flex, Text, TextStyle, useBreakpoint } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { BANNER_SOURCE_ICONS } from 'components/Banners/components/Banner/sourceIcons';

import { CampaignFrontendStatus } from 'store/models/campaignsDashboard';

const TrlKeys = {
    active: 'BannersPage.banner.status.active',
    paused: 'BannersPage.banner.status.paused',
    banned: 'BannersPage.banner.status.banned',
    onModeration: 'BannersPage.banner.status.onModeration',
};

enum BannerDisplayStatus {
    Active = 'active',
    Paused = 'paused',
    Banned = 'banned',
}

const STATUS_STYLE: Record<BannerDisplayStatus, TextStyle> = {
    [BannerDisplayStatus.Active]: 'positive',
    [BannerDisplayStatus.Paused]: 'warning',
    [BannerDisplayStatus.Banned]: 'negative',
};

interface BannerStatusProps {
    enabled: boolean;
    moderationStatus: BannerModerationStatus;
    states: BannerModerationState[];
    campaignStatus: CampaignFrontendStatus;
}

export const BannerStatus: FC<BannerStatusProps> = ({ enabled, moderationStatus, states, campaignStatus }) => {
    const { isMobile } = useBreakpoint();
    const trls = useTrls();

    const isCampaignPaused =
        campaignStatus === CampaignFrontendStatus.Blocked ||
        campaignStatus === CampaignFrontendStatus.BudgetExceeded ||
        campaignStatus === CampaignFrontendStatus.DailyBudgetExceedStatus;

    let bannerStatus = BannerDisplayStatus.Active;

    if (!enabled || isCampaignPaused) {
        bannerStatus = BannerDisplayStatus.Paused;
    } else if (moderationStatus === BannerModerationStatus.Banned) {
        bannerStatus = BannerDisplayStatus.Banned;
    }

    const sourcesOnModeration = [
        ...new Set(
            states
                .filter(({ status }) => status === BannerModerationStatus.New)
                .map(({ source }) => source)
                .filter((source) => BANNER_SOURCE_ICONS[source])
        ),
    ];

    const statusTypography = getBannerTextTypography(isMobile);

    return (
        <Flex direction={isMobile ? 'column' : 'row'} gap={isMobile ? 4 : 8} align={isMobile ? 'start' : 'center'}>
            <Text typography={statusTypography} style={STATUS_STYLE[bannerStatus]}>
                {trls(TrlKeys[bannerStatus])}
            </Text>
            {sourcesOnModeration.length > 0 && (
                <Flex gap={8} align="center">
                    {!isMobile && (
                        <Text typography={statusTypography} style="secondary">
                            {DOT}
                        </Text>
                    )}
                    <Text typography={statusTypography} style="primary">
                        {trls(TrlKeys.onModeration)}
                    </Text>
                    <Flex gap={4} align="center">
                        {sourcesOnModeration.map((source) => {
                            const sourceIcons = BANNER_SOURCE_ICONS[source];
                            if (!sourceIcons) return null;
                            const { mobile } = sourceIcons;
                            return <img key={source} src={mobile} alt={source} />;
                        })}
                    </Flex>
                </Flex>
            )}
        </Flex>
    );
};
